import { FC } from 'react';
import { Route, Switch } from 'react-router-dom';

import { LockedReason } from '__generated__/GQL';
import constructionSiteImg from 'assets/images/construction-site.svg';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useIsAccountHolder from 'common/hooks/useIsAccountHolder';
import Text from 'components/_core/Text';
import DecorativeImage from 'components/DecorativeImage';
import Loader from 'components/Loader';
import * as ContentLayout from 'layouts/ContentLayout';

import ClosureCancelation from './ClosureCancelation';
import ClosureCancelationConfirmation from './ClosureCancelationConfirmation';
import Card from './components/Card';
import Title from './components/Title';
import { useLockedReason } from './hooks';
import locales from './locales';

const AccountLocked: FC = () => {
  const { companyProfileId } = useCompanyContext();
  const {
    error: errorLockedReason,
    loading: loadingLockedReason,
    lockedCustomMessage,
    lockedReason,
  } = useLockedReason({ companyProfileId });
  const {
    error: errorAccountHolder,
    isAccountHolder,
    loading: loadingAccountHolder,
  } = useIsAccountHolder();

  const loading = loadingLockedReason || loadingAccountHolder;

  if (loading) {
    return (
      <ContentLayout.Root>
        <ContentLayout.Body>
          <Card>
            <Loader />
          </Card>
        </ContentLayout.Body>
      </ContentLayout.Root>
    );
  }

  if (
    errorLockedReason ||
    errorAccountHolder ||
    !lockedReason ||
    isAccountHolder === undefined
  ) {
    throw new Error('Locked reason is not defined');
  }

  return (
    <ContentLayout.Root>
      <ContentLayout.Body>
        {lockedReason === LockedReason.AccountClosureRequest &&
        isAccountHolder ? (
          <Switch>
            <Route
              component={ClosureCancelationConfirmation}
              path="/closure-cancel-confirmation"
            />
            <Route component={ClosureCancelation} />
          </Switch>
        ) : (
          <Card>
            <DecorativeImage src={constructionSiteImg} />
            <Title>{locales.title}</Title>
            <Text size="sm">
              {locales.formatString(
                locales[lockedReason as keyof typeof locales] as string,
                lockedCustomMessage || '',
              )}
            </Text>
          </Card>
        )}
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default AccountLocked;
